import { useCallback, useEffect, useState } from "react";
import { Receipt, CheckCircle2, FileClock, AlertTriangle, ExternalLink } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { fmtTL } from "../../lib/format";
import { toast } from "../../lib/notify";
import BulkClaimPreview from "./BulkClaimPreview";

const STATUS = {
  missing: { label: "Hakediş yok", cls: "bg-red-100 text-red-800", Icon: AlertTriangle },
  draft: { label: "Taslak", cls: "bg-amber-100 text-amber-800", Icon: FileClock },
  approved: { label: "Oluşturuldu", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
};

// Proje seçilince: dönemde iş yapmış taşeronlar ve hakediş durumu; tek tek ya da "Tümü için oluştur"
export default function MissingSubClaims({ project_id, category_id, region_ids, date_from, date_to, selectedSub, onPick, onCreated, reloadKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);

  const load = useCallback(() => {
    if (!project_id) { setData(null); return; }
    const params = { project_id };
    if (category_id) params.category_id = category_id;
    if (region_ids?.length) params.region_ids = region_ids.join(",");
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    http.get("/reports/subcontractor-claim/missing", { params }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [project_id, category_id, region_ids, date_from, date_to]);
  useEffect(load, [load, reloadKey]);

  if (!project_id || !data) return null;
  const body = { project_id, region_ids: region_ids?.length ? region_ids : null, date_from: date_from || null, date_to: date_to || null };
  const previewParams = { project_id, ...(category_id && { category_id }), ...(region_ids?.length && { region_ids: region_ids.join(",") }), ...(date_from && { date_from }), ...(date_to && { date_to }) };

  const createOne = async (r) => {
    if (!window.confirm(`${r.subcontractor_name} — ${r.category_name} için ${fmtTL(r.total)} tutarında taşeron hakedişi oluşturulsun mu?`)) return;
    setBusy(`${r.subcontractor_id}|${r.category_id}`);
    try {
      const { data: inv } = await http.post("/reports/subcontractor-claim/create-invoice", { ...body, subcontractor_id: r.subcontractor_id, category_id: r.category_id || category_id || null });
      toast.success(`${inv.invoice_no} oluşturuldu — ${r.subcontractor_name}`);
      load(); onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="bg-white border-2 border-amber-200 rounded-lg overflow-hidden" data-testid="missing-sub-claims">
      <BulkClaimPreview open={preview} onClose={() => setPreview(false)} params={previewParams} body={{ ...body, category_id: category_id || null }} onDone={() => { load(); onCreated?.(); }} />
      <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-sm font-bold text-slate-900">Taşeron Hakedişleri — {data.period}</div>
          <div className="text-xs text-slate-600">Bu projede dönemde iş yapan {data.rows.length} taşeron{category_id ? "" : " / ana başlık"} · eksik <b className={data.missing ? "text-red-700" : "text-emerald-700"} data-testid="missing-sub-claims-count">{data.missing}</b>{data.missing > 0 && <> · {fmtTL(data.missing_total)}</>}</div>
        </div>
        {data.missing > 0 && (
          <button type="button" onClick={() => setPreview(true)} disabled={!!busy} data-testid="btn-create-all-sub-claims" title="Önce önizleme: her taşeronun tutarı ve engeller listelenir, onayla üretilir"
            className="ml-auto inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors">
            <Receipt className="w-4 h-4" /> Tümü için oluştur ({data.missing})
          </button>
        )}
      </div>
      {data.rows.length === 0 ? (
        <div className="px-4 py-4 text-sm text-slate-500" data-testid="missing-sub-claims-empty">Bu proje ve dönemde taşeron kaydı yok.</div>
      ) : (
        <table className="w-full tbl">
          <thead className="bg-white"><tr>{["Taşeron", "Ana Başlık", "Kayıt", "Tutar", "Durum", ""].map((h, i) => <th key={h || i} className={`px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold ${i === 2 || i === 3 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
          <tbody>
            {data.rows.map((r) => {
              const st = STATUS[r.status]; const key = `${r.subcontractor_id}|${r.category_id}`; const sel = selectedSub === r.subcontractor_id;
              return (
                <tr key={key} className={`border-t border-slate-100 ${sel ? "bg-amber-50/70" : "hover:bg-slate-50"}`} data-testid={`missing-sub-row-${r.subcontractor_id}`} data-status={r.status}>
                  <td className="px-3 py-1.5 text-xs font-semibold cursor-pointer hover:underline" onClick={() => onPick(r)} title="Bu taşeronu seç (ayrıntılı hakediş)">{r.subcontractor_name}</td>
                  <td className="px-3 py-1.5 text-xs">{r.category_name}</td>
                  <td className="numeric-cell px-3 py-1.5 text-xs">{r.count}</td>
                  <td className="numeric-cell px-3 py-1.5 text-xs font-semibold">{fmtTL(r.total)}</td>
                  <td className="px-3 py-1.5 text-xs"><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${st.cls}`}><st.Icon className="w-3 h-3" /> {st.label}{r.invoice_no ? ` · ${r.invoice_no}` : ""}</span></td>
                  <td className="px-3 py-1.5 text-xs text-right whitespace-nowrap">
                    <button type="button" onClick={() => onPick(r)} className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 mr-1" data-testid={`missing-sub-pick-${r.subcontractor_id}`}>Seç</button>
                    {r.status === "missing" && <button type="button" onClick={() => createOne(r)} disabled={!!busy} className="px-2 py-1 rounded bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50" data-testid={`missing-sub-create-${r.subcontractor_id}`}>{busy === key ? "…" : "Oluştur"}</button>}
                    {r.invoice_id && <a href={`/veri-girisi?bolum=fatura&kayit=${r.invoice_id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 hover:bg-slate-100" data-testid={`missing-sub-invoice-${r.subcontractor_id}`}><ExternalLink className="w-3 h-3" /> Fatura</a>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
